import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactDOM from "react-dom";
import supabase from "../../supabaseClient";

const BILLING_NOTIFICATION_TYPE = "billing upload";

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return "Just now";
  const createdAt = new Date(timestamp);
  const diffMs = Date.now() - createdAt.getTime();
  const minutes = Math.max(1, Math.round(diffMs / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const playNotificationSound = () => {
  if (typeof window === "undefined") return;

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return;

  try {
    const audioContext = new AudioContextClass();
    const masterGain = audioContext.createGain();
    masterGain.gain.setValueAtTime(0.06, audioContext.currentTime);
    masterGain.connect(audioContext.destination);

    const scheduleNote = (frequency, startAt, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(frequency, startAt);

      gainNode.gain.setValueAtTime(0.0001, startAt);
      gainNode.gain.exponentialRampToValueAtTime(0.18, startAt + 0.012);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

      oscillator.connect(gainNode);
      gainNode.connect(masterGain);
      oscillator.start(startAt);
      oscillator.stop(startAt + duration);
    };

    const start = audioContext.currentTime;
    scheduleNote(1046.5, start, 0.12);
    scheduleNote(1318.5, start + 0.14, 0.16);

    window.setTimeout(() => {
      audioContext.close().catch(() => {});
    }, 450);
  } catch (error) {
    console.warn("Billing notification sound playback failed:", error);
  }
};

const BillingNotificationsBell = ({
  user,
  billingListPath = "",
  title = "Billing Notifications",
  className = "",
}) => {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const userId = user?.user_id || user?.id || "anonymous";
  const storageKey = `billing-notifications-seen-at:${userId}`;
  const sessionToastKey = `billing-notifications-toast-shown:${userId}`;

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [seenAt, setSeenAt] = useState(() => localStorage.getItem(storageKey) || "");
  const [alertOpen, setAlertOpen] = useState(false);

  const fetchNotifications = async () => {
    const { data, error } = await supabase
      .from("notification")
      .select("*")
      .eq("type", BILLING_NOTIFICATION_TYPE)
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(20);

    if (error) {
      console.error("Failed to fetch billing notifications:", error);
      return;
    }

    setNotifications(data || []);
  };

  useEffect(() => {
    fetchNotifications();

    const channel = supabase
      .channel(`billing-notifications-${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notification" },
        async (payload) => {
          if (payload.new?.type === BILLING_NOTIFICATION_TYPE || payload.old?.type === BILLING_NOTIFICATION_TYPE) {
            await fetchNotifications();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSeenAt(localStorage.getItem(storageKey) || "");
  }, [storageKey]);

  const unseenCount = useMemo(() => {
    if (!notifications.length) return 0;
    if (!seenAt) return notifications.length;
    const seenTime = new Date(seenAt).getTime();
    return notifications.filter((notification) => {
      const createdAt = new Date(notification.created_at).getTime();
      return createdAt > seenTime;
    }).length;
  }, [notifications, seenAt]);

  useEffect(() => {
    if (!unseenCount) return;

    const latestNotificationAt = notifications[0]?.created_at;
    if (!latestNotificationAt) return;

    const lastToastAt = sessionStorage.getItem(sessionToastKey);
    if (lastToastAt === latestNotificationAt) return;

    sessionStorage.setItem(sessionToastKey, latestNotificationAt);
    playNotificationSound();
    setAlertOpen(true);
  }, [notifications, sessionToastKey, unseenCount]);

  const markAsSeen = () => {
    const latestTimestamp = notifications[0]?.created_at;
    if (!latestTimestamp) return;
    localStorage.setItem(storageKey, latestTimestamp);
    setSeenAt(latestTimestamp);
  };

  const handleToggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen) {
      markAsSeen();
    }
  };

  const handleOpenBillingList = () => {
    if (!billingListPath) return;
    setOpen(false);
    setAlertOpen(false);
    navigate(billingListPath);
  };

  return (
    <div ref={panelRef} className={`relative ${className}`}>
      {alertOpen
        ? ReactDOM.createPortal(
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/45 px-4">
              <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-500">
                    <i className="fas fa-bell text-lg" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-lg font-semibold text-slate-900">
                      Notification Alert
                    </div>
                    <div className="mt-2 text-sm leading-6 text-slate-600">
                      You have notifications. Check inbox.
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {unseenCount} new billing notification{unseenCount === 1 ? "" : "s"} available.
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setAlertOpen(false)}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Dismiss
                  </button>
                  {billingListPath ? (
                    <button
                      type="button"
                      onClick={handleOpenBillingList}
                      className="rounded-2xl bg-blue px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue/90"
                    >
                      Open Inbox
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setAlertOpen(false);
                        setOpen(true);
                        markAsSeen();
                      }}
                      className="rounded-2xl bg-blue px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue/90"
                    >
                      View Notifications
                    </button>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <button
        type="button"
        onClick={handleToggle}
        className="relative inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-blue shadow-sm transition hover:bg-slate-50"
        aria-label={title}
      >
        <i className="fas fa-bell text-base" />
        {unseenCount > 0 ? (
          <>
            <span className="absolute right-2 top-2 h-3 w-3 rounded-full bg-red-500 ring-2 ring-white" />
            <span className="absolute -right-1 -top-1 min-w-[1.25rem] rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
              {unseenCount > 9 ? "9+" : unseenCount}
            </span>
          </>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-40 mt-3 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
          <div className="border-b border-slate-100 px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-blue">{title}</div>
                <div className="mt-1 text-xs text-slate-500">
                  Alerts for newly uploaded billing documents.
                </div>
              </div>
              <div className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                {notifications.length}
              </div>
            </div>
          </div>

          <div className="max-h-[360px] space-y-3 overflow-y-auto px-4 py-4">
            {notifications.length ? (
              notifications.map((notification) => (
                <div
                  key={notification.id || `${notification.created_at}-${notification.message}`}
                  className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue/10 text-blue">
                      <i className="fas fa-file-invoice-dollar text-sm" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="break-words text-sm font-semibold leading-5 text-slate-900">
                        {notification.message}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                        <span className="break-words">
                          by {notification.user_name || "System"}
                        </span>
                        <span className="text-slate-300">•</span>
                        <span>{formatTimeAgo(notification.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
                No billing notifications yet.
              </div>
            )}
          </div>

          {billingListPath ? (
            <div className="border-t border-slate-100 px-4 py-4">
              <button
                type="button"
                onClick={handleOpenBillingList}
                className="w-full rounded-2xl bg-blue px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue/90"
              >
                Open Billing List
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default BillingNotificationsBell;
